import { CookiesEvents } from 'actions';
import {
  FAVORITE_DELIVERY_TYPE,
  FAVORITE_DELIVERY_TYPE_COOKIE,
  ONE_DAY_COOKIE_EXPIRED_DATE,
  PRIORITY_STORE_IDS_COOKIE,
  THIRTY_DAYS_COOKIE_EXPIRED_DATE,
} from 'common/constants';
import { FavoriteAddressState } from 'types/favorite-address';
import { hasFavoriteAddressCookies, isFavoriteAddressEnabled } from 'selectors';
import { Events, Store } from 'store/types';
import { StoreonDispatch } from 'storeon';
import { getDomain } from './get-domain';

type CookieParams = {
  path: string;
  domain: string;
  expires: Date;
};

type ApplyFavoriteAddressCookiesResult = {
  deliveryTypeChanged: boolean;
};

const getFavoriteAddressCookieParams = (): CookieParams => ({
  path: '/',
  domain: `.${getDomain(__IS_SERVER__ ? '' : globalThis.location.hostname)}`,
  expires: new Date(Date.now() + ONE_DAY_COOKIE_EXPIRED_DATE),
});

const removeExistingCookie = (
  dispatch: StoreonDispatch<Events>,
  cookies: Record<string, string>,
  key: string,
  params: CookieParams,
) => {
  if (!cookies[key]) {
    return;
  }

  dispatch(CookiesEvents.remove, { key, params });
};

const updateCookieOnChange = (
  dispatch: StoreonDispatch<Events>,
  cookies: Record<string, string>,
  key: string,
  value: string,
  params: CookieParams,
) => {
  if (cookies[key] === value) {
    return;
  }

  dispatch(CookiesEvents.update, { key, value, params });
};

const applyRegionCookies = (
  dispatch: StoreonDispatch<Events>,
  cookies: Record<string, string>,
  params: CookieParams,
) => {
  updateCookieOnChange(
    dispatch,
    cookies,
    FAVORITE_DELIVERY_TYPE_COOKIE,
    FAVORITE_DELIVERY_TYPE.REGION,
    params,
  );
  removeExistingCookie(dispatch, cookies, PRIORITY_STORE_IDS_COOKIE, params);
};

const applyPriorityStoreIds = (
  dispatch: StoreonDispatch<Events>,
  cookies: Record<string, string>,
  priorityStoreIds: string[],
  params: CookieParams,
) => {
  const value = priorityStoreIds.join(',');

  if (!value) {
    removeExistingCookie(dispatch, cookies, PRIORITY_STORE_IDS_COOKIE, params);
    return;
  }

  updateCookieOnChange(dispatch, cookies, PRIORITY_STORE_IDS_COOKIE, value, params);
};

const getPriorityStoreIdsForCookies = (state: FavoriteAddressState): string[] | null => {
  if (state.priorityStoreIds !== null) {
    return state.priorityStoreIds;
  }

  return null;
};

export const applyFavoriteAddressCookies = (
  dispatch: StoreonDispatch<Events>,
  cookies: Record<string, string>,
  state: FavoriteAddressState,
): ApplyFavoriteAddressCookiesResult => {
  if (state.unavailable) {
    return { deliveryTypeChanged: false };
  }

  const params = getFavoriteAddressCookieParams();
  const nextDeliveryType =
    state.deliveryType === FAVORITE_DELIVERY_TYPE.REGION || state.shouldResetToRegion
      ? FAVORITE_DELIVERY_TYPE.REGION
      : state.deliveryType;
  const deliveryTypeChanged = cookies[FAVORITE_DELIVERY_TYPE_COOKIE] !== nextDeliveryType;

  if (nextDeliveryType === FAVORITE_DELIVERY_TYPE.REGION) {
    applyRegionCookies(dispatch, cookies, {
      ...params,
      expires: new Date(Date.now() + THIRTY_DAYS_COOKIE_EXPIRED_DATE),
    });

    return { deliveryTypeChanged };
  }

  updateCookieOnChange(dispatch, cookies, FAVORITE_DELIVERY_TYPE_COOKIE, nextDeliveryType, {
    ...params,
    expires: new Date(Date.now() + THIRTY_DAYS_COOKIE_EXPIRED_DATE),
  });

  const priorityStoreIds = getPriorityStoreIdsForCookies(state);

  if (priorityStoreIds !== null) {
    applyPriorityStoreIds(dispatch, cookies, priorityStoreIds, params);

    return { deliveryTypeChanged };
  }

  if (state.deliveryType === FAVORITE_DELIVERY_TYPE.PICKUP_POINT) {
    removeExistingCookie(dispatch, cookies, PRIORITY_STORE_IDS_COOKIE, params);
  }

  return { deliveryTypeChanged };
};

export const clearFavoriteAddressCookiesIfDisabled = (store: Store) => {
  const cookies = store.get().cookies;

  if (isFavoriteAddressEnabled(cookies) || !hasFavoriteAddressCookies(cookies)) {
    return;
  }

  const params = getFavoriteAddressCookieParams();

  if (cookies[FAVORITE_DELIVERY_TYPE_COOKIE]) {
    store.dispatch(CookiesEvents.remove, { key: FAVORITE_DELIVERY_TYPE_COOKIE, params });
  }

  if (cookies[PRIORITY_STORE_IDS_COOKIE]) {
    store.dispatch(CookiesEvents.remove, { key: PRIORITY_STORE_IDS_COOKIE, params });
  }
};
